/** How a **Unit** — one individually-identifiable article within an Order line item — is encoded and
 *  named. Homed in `lib` rather than a feature so `device-params` can share it without closing an
 *  `orders` cycle (ADR-0024); `lib` imports only `lib`/`types`, so that is true by construction. The
 *  **two-part** replacement codec stays in `features/orders/replacements/format.ts` — ADR-0023
 *  rejected merging the two. */
const SEPARATOR = '#';

/**
 * A Unit's identity is the **triple** (line item, unit index, Bundle component). Eligible-item
 * responses return a Bundle's components as separate entries *sharing one `orderItemId`* — four of
 * them in the recorded sample, each with `unitIndex: 1` — so a two-part `${orderItemId}#${unitIndex}`
 * encoding collides across all four and would make the raise POST ambiguous.
 */
export function warrantyUnitValue(
  orderItemId: string,
  unitIndex: number,
  bundleDeviceId: string | null,
): string {
  return `${orderItemId}${SEPARATOR}${unitIndex}${SEPARATOR}${bundleDeviceId ?? ''}`;
}

/** Precondition: `value` came from `warrantyUnitValue` — the select's options are its only source.
 *  Both ids are UUIDs, so `#` cannot occur inside either and a plain three-way split is exact. An
 *  absent Bundle component round-trips through the empty third segment back to `null`. */
export function parseWarrantyUnitValue(value: string): {
  orderItemId: string;
  unitIndex: number;
  bundleDeviceId: string | null;
} {
  const [orderItemId, unitIndex, bundleDeviceId] = value.split(SEPARATOR);
  return {
    orderItemId,
    unitIndex: Number(unitIndex),
    bundleDeviceId: bundleDeviceId || null,
  };
}

/**
 * How a Unit is named, everywhere. The number is suffixed **only** when the line item holds more
 * than one unit, so a single unit reads "Smart Thermostat" and ten read "Hydro Pump 1" …
 * "Hydro Pump 10" (AC 112366). One helper because the numbering must match "the numbering used to
 * identify units elsewhere in the order" — the intake dropdown, the listing row and the Ticket
 * Preview must not each invent a format.
 *
 * `quantity` is the line item's total, not the eligible count: units already requested leave a gap
 * rather than re-sequencing the rest, so "Hydro Pump 7" means the same physical device here as in
 * tracking and installation (`CONTEXT.md`, "Unit").
 */
export function unitLabel(itemName: string, quantity: number, unitIndex: number): string {
  return quantity > 1 ? `${itemName} ${unitIndex}` : itemName;
}
